import type { BinaryTreeNode } from '@alpha/api-client';
import { useState } from 'react';
import { formatDate } from '../../lib/format';
import './BinaryTreeView.css';

export interface BinaryTreeViewProps {
  root: BinaryTreeNode | null | undefined;
  /**
   * Called when the viewer drills into a node whose leg counts imply more
   * descendants than the currently-loaded depth shows (see `isTruncated`
   * below). The page owns re-fetching a fresh, deeper tree rooted there.
   */
  onDrill?: (node: BinaryTreeNode) => void;
  /** Node currently being re-rooted into, so its card can show a spinner state. */
  pendingDrillId?: string | null;
}

/**
 * A node is "truncated" when the backend reports members in a leg
 * (left_count/right_count, computed independent of render depth) but that
 * child wasn't included in this response because the depth cap was hit.
 * A leg with count 0 and a null child is a genuinely open placement slot.
 */
function isTruncated(node: BinaryTreeNode, leg: 'left' | 'right'): boolean {
  return leg === 'left' ? node.left_count > 0 && !node.left : node.right_count > 0 && !node.right;
}

export function BinaryTreeView({ root, onDrill, pendingDrillId }: BinaryTreeViewProps) {
  if (!root) return null;

  return (
    <div className="btv-scroll">
      <div className="btv-tree">
        <Branch node={root} onDrill={onDrill} pendingDrillId={pendingDrillId} isRoot />
      </div>
    </div>
  );
}

function Branch({
  node,
  onDrill,
  pendingDrillId,
  isRoot,
}: {
  node: BinaryTreeNode;
  onDrill?: (node: BinaryTreeNode) => void;
  pendingDrillId?: string | null;
  isRoot?: boolean;
}) {
  const hasChildRow = true; // every node always shows a left + right slot

  return (
    <div className="btv-branch">
      <NodeCard node={node} onDrill={onDrill} pendingDrillId={pendingDrillId} isRoot={isRoot} />
      {hasChildRow && (
        <div className="btv-children">
          <div className="btv-child">
            <ChildSlot node={node} leg="left" onDrill={onDrill} pendingDrillId={pendingDrillId} />
          </div>
          <div className="btv-child">
            <ChildSlot node={node} leg="right" onDrill={onDrill} pendingDrillId={pendingDrillId} />
          </div>
        </div>
      )}
    </div>
  );
}

function ChildSlot({
  node,
  leg,
  onDrill,
  pendingDrillId,
}: {
  node: BinaryTreeNode;
  leg: 'left' | 'right';
  onDrill?: (node: BinaryTreeNode) => void;
  pendingDrillId?: string | null;
}) {
  const child = leg === 'left' ? node.left : node.right;

  if (child) {
    return <Branch node={child} onDrill={onDrill} pendingDrillId={pendingDrillId} />;
  }

  if (isTruncated(node, leg)) {
    const count = leg === 'left' ? node.left_count : node.right_count;
    const pending = pendingDrillId === node.user_id;
    return (
      <button
        type="button"
        className="btv-more"
        disabled={pending}
        onClick={() => onDrill?.(node)}
        title={`${count} member${count === 1 ? '' : 's'} in this leg — click to load deeper`}
      >
        {pending ? 'Loading…' : `+${count} more`}
      </button>
    );
  }

  return (
    <div className="btv-node btv-node-empty">
      <span className="btv-empty-label">Open {leg}</span>
    </div>
  );
}

function NodeCard({
  node,
  onDrill,
  pendingDrillId,
  isRoot,
}: {
  node: BinaryTreeNode;
  onDrill?: (node: BinaryTreeNode) => void;
  pendingDrillId?: string | null;
  isRoot?: boolean;
}) {
  const [tapped, setTapped] = useState(false);
  const canDrillDeeper = (isTruncated(node, 'left') || isTruncated(node, 'right')) && !!onDrill;

  return (
    <div
      className={`btv-node ${node.is_activated ? 'btv-active' : 'btv-inactive'} ${isRoot ? 'btv-root' : ''}`}
      onClick={() => setTapped((t) => !t)}
      tabIndex={0}
      role="button"
      aria-label={`${node.user_code} — ${node.name}`}
    >
      <span className={`btv-dot ${node.is_activated ? 'btv-dot-active' : 'btv-dot-inactive'}`} />
      <strong className="btv-code">{node.user_code}</strong>
      <span className="btv-name">{node.name || '—'}</span>
      <span className="btv-legs">
        L {node.left_count} · R {node.right_count}
      </span>
      {canDrillDeeper && (
        <button
          type="button"
          className="btv-drill"
          onClick={(e) => {
            e.stopPropagation();
            onDrill?.(node);
          }}
          disabled={pendingDrillId === node.user_id}
        >
          {pendingDrillId === node.user_id ? 'Loading…' : 'View deeper ↓'}
        </button>
      )}

      <div className={`btv-popup ${tapped ? 'btv-popup-tapped' : ''}`}>
        <div className="btv-popup-row">
          <span className="btv-popup-label">User ID</span>
          <span className="btv-popup-value">{node.user_code}</span>
        </div>
        <div className="btv-popup-row">
          <span className="btv-popup-label">Name</span>
          <span className="btv-popup-value">{node.name || '—'}</span>
        </div>
        <div className="btv-popup-row">
          <span className="btv-popup-label">Status</span>
          <span className={node.is_activated ? 'btv-popup-good' : 'btv-popup-warn'}>
            {node.is_activated ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className="btv-popup-row">
          <span className="btv-popup-label">Position</span>
          <span className="btv-popup-value">
            {node.position ? (node.position === 'left' ? 'Left leg' : 'Right leg') : 'Root'}
          </span>
        </div>
        <div className="btv-popup-row">
          <span className="btv-popup-label">Joined</span>
          <span className="btv-popup-value">{formatDate(node.created_at)}</span>
        </div>
        <div className="btv-popup-row">
          <span className="btv-popup-label">Activated</span>
          <span className="btv-popup-value">{node.activated_at ? formatDate(node.activated_at) : 'Not yet'}</span>
        </div>
        <div className="btv-popup-row">
          <span className="btv-popup-label">Left count</span>
          <span className="btv-popup-value">{node.left_count}</span>
        </div>
        <div className="btv-popup-row">
          <span className="btv-popup-label">Right count</span>
          <span className="btv-popup-value">{node.right_count}</span>
        </div>
      </div>
    </div>
  );
}
